import { ExperienceResponse } from '@bookingkit-private/api-v4/models/experience-response';
import { InventoryServiceError } from '@/services/InventoryService/Errors/InventoryServiceError';
import {Experience, ExperienceOption, FlexOffer, Image} from '@/services/InventoryService/types';
import { ExperienceOptionInfo, ImageResponse } from '@bookingkit-private/api-v4';
import type {FlexOfferResponse} from "@bookingkit-private/api-v4/models/flex-offer-response";

export const mapExperienceOption = (option: ExperienceOptionInfo): ExperienceOption => {
    if (!option.id || typeof option.id !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('option', 'string', option?.id);
    }

    if (typeof option.duration_in_minutes !== 'number') {
        throw InventoryServiceError.fromInvalidProperty('option duration_in_minutes', 'number', option?.duration_in_minutes);
    }

    const cutOffBeforeBegin = option.restrictions?.cutoff_before_begin ?? undefined;
    const cutOffBeforeEnd = option.restrictions?.cutoff_before_end ?? undefined;

    return {
        id: option.id,
        active: option?.active !== undefined ? option.active : true,
        durationInMinutes: option.duration_in_minutes,
        cutOffBeforeBegin,
        cutOffBeforeEnd,
        title: option.title as string
    };
};

export const mapCoverImage = (coverImage: ImageResponse | undefined): Image | undefined => {
    if (coverImage) {
        if (typeof coverImage.id !== 'string') {
            throw InventoryServiceError.fromInvalidProperty('coverImage', 'string', coverImage.id);
        }
        if (!coverImage.sizes) {
            throw InventoryServiceError.fromInvalidProperty('coverImage', 'object', coverImage.sizes);
        }
        if (typeof coverImage.sizes?.small !== 'string') {
            throw InventoryServiceError.fromInvalidProperty('coverImage small url', 'string', coverImage.sizes?.small);
        }
        if (typeof coverImage.sizes?.medium !== 'string') {
            throw InventoryServiceError.fromInvalidProperty('coverImage medium url', 'string', coverImage.sizes?.medium);
        }
        if (typeof coverImage.sizes?.large !== 'string') {
            throw InventoryServiceError.fromInvalidProperty('coverImage large url', 'string', coverImage.sizes?.large);
        }
        if (typeof coverImage.sizes?.maximum !== 'string') {
            throw InventoryServiceError.fromInvalidProperty('coverImage maximum url', 'string', coverImage.sizes?.maximum);
        }
        return {
            id: coverImage.id,
            sizes: {
                small: coverImage.sizes.small,
                medium: coverImage.sizes.medium,
                large: coverImage.sizes.large,
                maximum: coverImage.sizes.maximum,
            },
        };
    }

    return undefined;
};

export const fromApiModel = (fromApiModelInput:ExperienceResponse):Experience => {
    const {
        id, title, options, cover_image,
    } = fromApiModelInput;

    if (typeof id !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('id', 'string', id);
    }
    if (typeof title !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('title', 'string', title);
    }
    if (!options) {
        throw InventoryServiceError.fromInvalidProperty('options', 'Object', options);
    }
    return {
        id,
        title,
        options: options.map((o) => mapExperienceOption(o)),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        coverImage: mapCoverImage(cover_image),
    };
};

export const fromApiToFlexOffer = (fromApiModelInput:FlexOfferResponse):FlexOffer => {
    const {
        id, name, description, experience_id, option_id
    } = fromApiModelInput;

    if (typeof id !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('id', 'string', id);
    }
    if (typeof name !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('title', 'string', name);
    }
    if (typeof experience_id !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('experience_id', 'string', experience_id);
    }
    if (typeof option_id !== 'string') {
        throw InventoryServiceError.fromInvalidProperty('option_id', 'string', option_id);
    }
    return {
        id,
        title : name,
        description: description ?? '',
        experience: experience_id,
        option: option_id,
    };
};

export default { fromApiModel, fromApiToFlexOffer };
